





import { Component, Watch, Mixins } from "vue-property-decorator";
import { mapGetters, mapState } from "vuex";
import { install as gtmInstall } from "@/boot/gtm";

// Mixins
import Context from "@/mixins/context";

// Enums/Data
import { BrandConfigKeys } from "@/data/constants";

// Types
import { IState } from "@/store";
import { IUser } from "@/models/users";
import { BModalComponent } from "buefy/types/components";

@Component({
  computed: {
    ...mapState({
      brandName: (state: IState) => state.brand.name,
      metaTitles: (state: IState) => state.ui.meta.titles,
      uiLoading: (state: IState) => state.ui.loading,
      user: (state: IState) => state.user
    }),
    ...mapGetters({
      brandColor: "brand/color",
      brandColorContrast: "brand/contrastColorHex",
      brandFavicon: "brand/favicon",
      isMultibrand: "brand/isMultibrand",
      isValidOAuthClient: "brand/isValidOAuthClient"
    })
  }
})
export default class App extends Mixins(Context) {
  // Vuex Bindings

  private brandColor!: string;
  private brandColorContrast!: string;
  private brandFavicon?: string;
  private brandName!: string;
  private isMultibrand!: boolean;
  private metaTitles!: [];
  private user!: IUser;
  public isValidOAuthClient!: boolean;
  public uiLoading!: boolean;

  // Data

  private invalidOAuthClientModal: BModalComponent | null = null;

  private get hasExpiredSession() {
    return this.$store.getters["auth/hasExpiredSession"](this.context);
  }

  @Watch("hasExpiredSession")
  private sessionExpired(isExpired: boolean) {
    if (isExpired && !this.$_.isEmpty(this.user)) {
      this.$store
        .dispatch("auth/tryReAuthentication", this.context)
        .catch(() => {
          this.$store.dispatch("auth/handleReAuthFail", this.context);
        });
    }
  }

  @Watch("brandColor")
  public brandColorChange() {
    this.setBrandColor();
  }

  @Watch("brandFavicon")
  public onFaviconChange() {
    this.setBrandFavicon();
  }

  @Watch("isValidOAuthClient", { immediate: true })
  public async onOAuthValidityChange(isValid) {
    if (!isValid && !this.invalidOAuthClientModal) {
      this.invalidOAuthClientModal = await this.$store.dispatch(
        "ui/openInvalidOAuthClientModal"
      );
    } else if (isValid && this.invalidOAuthClientModal) {
      await this.invalidOAuthClientModal.close();
      this.invalidOAuthClientModal = null;
    }
  }

  private get metaProps(): object {
    return this.$_.merge(
      {},
      this.$route.params,
      this.$store.state.ui.meta.props
    );
  }

  private get metaTitlesMapped(): any {
    return this.$_.map(this.metaTitles, title => {
      return this.$i18n.t(title, this.metaProps);
    });
  }

  private get metaTitle(): string {
    const isAdminApp: boolean = this.$route.path.startsWith("/admin");
    const prepend: string = isAdminApp
      ? `${this.$i18n.t("_.upmind")} –`
      : `${this.brandName} –`;
    const parts: string = this.metaTitlesMapped.join(" | ");
    const append: string =
      isAdminApp && this.brandName ? ` (${this.brandName})` : ``;
    return this.$_.trimEnd(
      `${prepend}${parts ? ` ${parts}` : ``}${append}`,
      ` – `
    );
  }

  @Watch("metaTitle")
  public metaTitleChanged() {
    document.title = this.metaTitle;
  }

  private created() {
    this.setBrandColor();
    this.setBrandFavicon();
    // Initialise GTM on client side
    if (!this.isAdmin) this.initGtm();
  }

  private async initGtm() {
    try {
      const key = BrandConfigKeys.ANALYTICS_GTM_CONTAINER_ID;
      await this.$store.dispatch("brand/getConfig", {
        keys: [key],
        brandId: this.$store.state.brand?.id
      });
      const containerId = this.$store.getters["brand/config"][key];
      if (containerId.length) gtmInstall(containerId);
    } catch {
      // Catch error as not to break app
    }
  }

  private mounted() {
    const appLoader = document.getElementById("app-loading");
    if (appLoader && appLoader.parentNode) {
      appLoader.parentNode.removeChild(appLoader);
    }
  }

  private setBrandColor() {
    document.documentElement.style.setProperty(
      "--brand-color",
      ` ${this.brandColor}`
    );
    document.documentElement.style.setProperty(
      "--brand-color-contrast",
      ` ${this.brandColorContrast}`
    );
  }

  private setBrandFavicon() {
    if (this.brandFavicon === undefined && !this.isMultibrand) return;
    const types = ["icon", "apple-touch-icon"];
    for (const type of types) {
      let link = document.querySelector(`link[rel~='${type}']`);
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", type);
        document.getElementsByTagName("head")[0].appendChild(link);
      }
      link.setAttribute(
        "href",
        this.brandFavicon
          ? this.brandFavicon + "?size=32x32"
          : "/assets/images/favicons/32x32.png"
      );
    }
  }
}
